import { useRef, type CSSProperties } from "react";
import { Check } from "lucide-react";
import { cn } from "../../lib/cn";

export const OTP_LENGTH = 6;

/**
 * The shortest a check should stay in `verifying`. A fast server would
 * otherwise answer mid-fold and snap the cells back before the badge shows;
 * callers hold their result until this much time has passed.
 */
export const OTP_MIN_VERIFY_MS = 1100;

/** Resolves `task` no sooner than OTP_MIN_VERIFY_MS after the call. */
export async function withOtpVerifyHold<T>(task: Promise<T>): Promise<T> {
  const [result] = await Promise.all([
    task,
    new Promise((resolve) => setTimeout(resolve, OTP_MIN_VERIFY_MS)),
  ]);
  return result;
}

/**
 * Where the code is in its check. `verifying` folds the cells into a spinning
 * badge, `success` swaps the spinner for a check mark; a rejected code is
 * `error` (the prop), which works in any status.
 */
export type OtpCellsStatus = "idle" | "verifying" | "success";

export interface OtpCellsProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onBlur?: () => void;
  /** Fires once the last empty cell is filled, with the full code. */
  readonly onComplete?: (value: string) => void;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly status?: OtpCellsStatus;
}

// Uneven leans so the row wobbles rather than swaying as one block.
const TILTS = ["-8deg", "6deg", "-5deg", "7deg", "-6deg", "8deg"];
const BURST_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

/**
 * Six boxed digit cells driving a single string field -- shared by the
 * login OTP screen, the forgot-password reset-OTP screen and the profile's
 * enable-2FA dialog (same Figma pattern, node 2676:425394, reused verbatim
 * since no flow needs its own visual variant). A single onChange fed a multi-character
 * value (paste, or a testing harness setting the whole code at once) is
 * distributed across all six cells.
 */
export function OtpCells({
  id,
  value,
  onChange,
  onBlur,
  onComplete,
  disabled,
  error,
  status = "idle",
}: OtpCellsProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const digits = Array.from({ length: OTP_LENGTH }, (_, i) => value[i] ?? "");
  const collapsed = status !== "idle";

  const commit = (next: string) => {
    onChange(next);
    if (next.length === OTP_LENGTH) onComplete?.(next);
  };

  const handleChange = (index: number, raw: string) => {
    const clean = raw.replace(/\D/g, "");

    if (clean.length <= 1) {
      const next = digits.slice();
      next[index] = clean;
      commit(next.join(""));
      if (clean && index < OTP_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }
      return;
    }

    const merged = clean.slice(0, OTP_LENGTH);
    commit(merged);
    inputsRef.current[Math.min(merged.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  return (
    // Grid divides the row evenly regardless of viewport width; the fixed
    // 73px cells only apply once there's room for them (md+).
    <div
      dir="ltr"
      aria-busy={status === "verifying" ? true : undefined}
      className="relative grid w-full grid-cols-6 gap-2 sm:gap-3 md:flex md:items-center md:justify-center md:gap-[1.125rem]"
    >
      {digits.map((digit, index) => (
        // md:basis-[4.5625rem] with grow-0/shrink (not shrink-0) lets all six
        // cells compress together, proportionally, if the form panel
        // ever has less than ~528px to give them, instead of overflowing
        // past the panel edge and rendering on top of the image panel.
        <div
          key={index}
          className="relative min-w-0 md:basis-[4.5625rem] md:grow-0 md:shrink"
        >
          {/* The rule the design threads between cells, spanning exactly
              the gap to the next one so it never runs past the row's ends. */}
          {index < OTP_LENGTH - 1 && (
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute start-full top-1/2 h-0.5 w-2 -translate-y-1/2 bg-otp-border transition-opacity duration-200 sm:w-3 md:w-[1.125rem]",
                collapsed && "opacity-0",
              )}
            />
          )}
          <input
            ref={(el) => {
              inputsRef.current[index] = el;
            }}
            id={index === 0 ? id : undefined}
            data-testid={`otp-cell-${index}`}
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            maxLength={OTP_LENGTH}
            value={digit}
            disabled={disabled || collapsed}
            aria-invalid={error ? true : undefined}
            onChange={(e) => {
              handleChange(index, e.target.value);
            }}
            onKeyDown={(e) => {
              handleKeyDown(index, e);
            }}
            onBlur={onBlur}
            style={
              {
                "--otp-tilt": TILTS[index],
                "--otp-shift": `calc(${2.5 - index} * (100% + 1rem))`,
              } as CSSProperties
            }
            className={cn(
              "relative block aspect-square w-full min-w-0 rounded-sm border-2 bg-container-default-background text-center text-heading-h4-semibold text-text caret-blue-purple-700 transition-[background-color,border-color,color,box-shadow] duration-200 ease-emphasized focus:border-blue-purple-700 focus:shadow-otp-focus focus-visible:outline-none disabled:cursor-not-allowed md:aspect-auto md:h-20",
              digit
                ? "border-blue-purple-600 bg-blue-purple-600 text-neutral-white caret-neutral-white shadow-otp-filled focus:border-blue-purple-700 focus:shadow-otp-focus-filled"
                : "border-otp-border",
              error &&
                "animate-otp-shake border-fail-text bg-container-default-background text-fail-text caret-fail-text shadow-otp-error focus:border-fail-text focus:shadow-otp-error",
              collapsed && "animate-otp-collapse",
              disabled && !collapsed && "opacity-50",
            )}
          />
        </div>
      ))}

      {collapsed && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="relative flex size-14 items-center justify-center rounded-full bg-blue-purple-600 shadow-otp-badge animate-in fade-in-0 zoom-in-50 duration-300 [animation-delay:320ms]">
            {status === "verifying" ? (
              <span className="absolute -inset-1.5 animate-spin rounded-full border-2 border-blue-purple-500 border-t-transparent" />
            ) : (
              <>
                {BURST_ANGLES.map((angle) => (
                  <span
                    key={angle}
                    style={{ "--otp-angle": `${angle}deg` } as CSSProperties}
                    className="absolute inset-0 m-auto size-1.5 rounded-full bg-blue-purple-500 animate-otp-burst"
                  />
                ))}
                <Check
                  className="size-7 text-neutral-white animate-in fade-in-0 zoom-in-50 duration-300 ease-spring"
                  strokeWidth={3}
                />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
